import type { AppBadgeColor } from '@shared/ui/badges'
import type { LessonRecurringDay, LessonRecurringPayload, LessonRecurringTutor } from './interfaces'

import { DateFormat } from '@brighterly/lib-core-types/date/enums'

import { useDayjs } from '@shared/lib/format'

import { LessonRecurringFinishType } from './enums'

/** One weekday of the form: its time is null until the admin picks one, which also ticks the day. */
export interface LessonRecurringDayForm {
  day: string
  startTime: string | null
  duration: number
  weekCycle: number
}

export interface LessonRecurringAvailabilityLabel {
  text: string
  color: AppBadgeColor
}

const dayjs = useDayjs()

const FULLY_AVAILABLE = 100
const PARTLY_AVAILABLE = 50

/** In the legacy order, Monday first. */
export const LESSON_RECURRING_DAYS: readonly { day: string, name: string }[] = [
  { day: 'Mon', name: 'Monday' },
  { day: 'Tue', name: 'Tuesday' },
  { day: 'Wed', name: 'Wednesday' },
  { day: 'Thu', name: 'Thursday' },
  { day: 'Fri', name: 'Friday' },
  { day: 'Sat', name: 'Saturday' },
  { day: 'Sun', name: 'Sunday' },
]

export const LESSON_RECURRING_WEEK_CYCLES: readonly number[] = [1, 2, 3]

export const createRecurringDays = (duration: number): LessonRecurringDayForm[] =>
  LESSON_RECURRING_DAYS.map(({ day }) => ({ day, startTime: null, duration, weekCycle: 1 }))

/** 'Mon' … 'Sun' of a 'YYYY-MM-DD' date. */
export const getWeekdayOfDate = (date: string): string => dayjs(date, DateFormat.DefaultDate).format('ddd')

export const getCheckedRecurringDays = (days: LessonRecurringDayForm[]): LessonRecurringDay[] =>
  days
    .filter((day): day is LessonRecurringDayForm & { startTime: string } => day.startTime !== null)
    .map(day => ({ day: day.day, start_time: day.startTime, duration: day.duration, week_cycle: day.weekCycle }))

/** Zero occurrences or lessons only puts the schedule in place for the discipline's tutor: no tutor is picked. */
export const isRecurringScheduleOnly = (finishType: LessonRecurringFinishType, occurrences: number, lessons: number): boolean =>
  (finishType === LessonRecurringFinishType.Occurrences && occurrences === 0)
  || (finishType === LessonRecurringFinishType.Lessons && lessons === 0)

export const buildRecurringPayload = (form: {
  startDate: string
  days: LessonRecurringDayForm[]
  finishType: LessonRecurringFinishType
  occurrences: number
  lessons: number
  finishDate: string
}): LessonRecurringPayload => {
  const finishValues: Record<LessonRecurringFinishType, string | number> = {
    [LessonRecurringFinishType.Occurrences]: form.occurrences,
    [LessonRecurringFinishType.Lessons]: form.lessons,
    [LessonRecurringFinishType.Date]: form.finishDate,
  }

  return {
    start_date: form.startDate,
    days: getCheckedRecurringDays(form.days),
    finish_type: form.finishType,
    finish_value: finishValues[form.finishType],
  }
}

export const getRecurringAvailabilityLabel = (tutor: LessonRecurringTutor): LessonRecurringAvailabilityLabel => {
  if (tutor.availability >= FULLY_AVAILABLE) return { text: 'Available: all', color: 'emerald' }
  if (tutor.availability >= PARTLY_AVAILABLE) return { text: `Partly busy: ${tutor.busy_count}/${tutor.total_count}`, color: 'amber' }

  return { text: `Busy: ${tutor.busy_count}/${tutor.total_count}`, color: 'red' }
}

export const isTutorFullyAvailable = (tutor: LessonRecurringTutor | null): boolean =>
  !!tutor && tutor.availability >= FULLY_AVAILABLE
